/**
 * API Client
 * Centralized fetch wrapper with error handling, retries, and type safety
 *
 * Uses Result<T, ApiError> pattern for type-safe error handling without exceptions.
 */

import { getCsrfToken, setCsrfToken } from '@/lib/auth/csrfToken';
import { extractSetCookieValue } from '@/lib/server-utils/authCookies';
import {
  API_CONFIG,
  API_ENDPOINTS,
  HTTP_STATUS,
  DEV_MODE,
  getEnvVar,
  getCookieName,
} from '@/lib/config/api.config';
import type { ApiError } from '@/lib/types/errors';
import { fromHttpResponse } from '@/lib/types/errors';
import { err, ok, type Result } from '@/lib/types/result';
import { logger } from '@/lib/utils/logger';

// TanStack Start SSR request types — intentionally removed (see L1 note in
// request()). apiClient no longer reads a (never-populated) global request.

// Outcome of a token-refresh attempt. Lets callers distinguish a hard logout
// (refresh token invalid/missing) from a transient failure worth retrying.
export type RefreshOutcome =
  | { status: 'refreshed' }
  | { status: 'unauthorized' }
  | { status: 'conflict'; retryAfterMs: number }
  | { status: 'failed' };

// A 409 means another tab or server instance has already rotated this browser's
// refresh token. Honor the server's bounded delay so the winning response has
// time to update the shared cookie jar before retrying the original request.
const REFRESH_CONFLICT_MIN_DELAY_MS = 250;
export const REFRESH_CONFLICT_DEFAULT_DELAY_MS = 1000;
export const REFRESH_CONFLICT_MAX_DELAY_MS = 5000;

export function parseRefreshRetryAfter(value: string | null): number {
  if (value === null) return REFRESH_CONFLICT_DEFAULT_DELAY_MS;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return REFRESH_CONFLICT_DEFAULT_DELAY_MS;
  return Math.min(
    Math.max(Math.ceil(seconds * 1000), REFRESH_CONFLICT_MIN_DELAY_MS),
    REFRESH_CONFLICT_MAX_DELAY_MS
  );
}

export const waitForRefreshConflict = (retryAfterMs: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, retryAfterMs));

// Poll cadence for the bounded waits on an in-flight CSRF fetch / token refresh.
const AUTH_WAIT_POLL_INTERVAL_MS = 100;

/**
 * Canonical message carried by the one 401 that means the session is gone for
 * good: the refresh endpoint itself rejected the refresh token (S1). Query
 * hooks re-wrap apiClient errors as `new Error(error.message)` (25+ call
 * sites), so the message is the only marker that survives to
 * QueryCache.onError; isSessionRevokedError() matches it.
 */
export const SESSION_REVOKED_ERROR_MESSAGE = 'Your session has ended. Please sign in again.';

export function isSessionRevokedError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    (error as { message?: unknown }).message === SESSION_REVOKED_ERROR_MESSAGE
  );
}

interface ApiConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
}

interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean | string[] | undefined>;
  timeout?: number;
  retry?: number;
  skipAuthRefresh?: boolean;
  /** Return the full parsed envelope instead of extracting its `data` field.
   * Needed for endpoints whose `pagination` is a sibling of `data`
   * (server `WriteSuccessWithPagination`), which the default unwrap drops. */
  raw?: boolean;
}

class ApiClient {
  private config: ApiConfig;
  private isRefreshing: boolean = false; // Prevent concurrent refresh calls
  // Outcome of the most recent refresh, so concurrent waiters can mirror it
  // instead of assuming success.
  private lastRefreshOutcome: RefreshOutcome = { status: 'refreshed' };
  private isFetchingCsrf: boolean = false; // Prevent concurrent CSRF fetches

  /**
   * Check if running in development mode (skip auth)
   */
  private isDevMode(): boolean {
    // Check DEV_MODE constant first
    if (DEV_MODE) return true;

    return false;
  }

  constructor(config: ApiConfig = {}) {
    const headers: Record<string, string> = {
      ...API_CONFIG.DEFAULT_HEADERS,
      ...config.headers,
    };

    this.config = {
      baseURL: config.baseURL || '',
      timeout: config.timeout || API_CONFIG.TIMEOUT_MS,
      headers,
      credentials: config.credentials || API_CONFIG.CREDENTIALS,
    };

    // Debug logging
    logger.info('API Client', 'ApiClient constructor', {
      headerNames: Object.keys(this.config.headers ?? {}),
      envVars: {
        VITE_DEV_MODE: getEnvVar('VITE_DEV_MODE', 'undefined'),
        DEV_MODE,
        baseURL: this.config.baseURL,
      },
    });
  }

  /**
   * Fetch CSRF token from backend if not available
   * This ensures CSRF token is available for state-changing requests
   * Uses an authenticated endpoint to trigger CSRF token generation
   * Returns the token if available, null otherwise
   */
  private async ensureCsrfToken(requestHeaders?: Record<string, string>): Promise<string | null> {
    // In dev skip-auth mode, don't block on CSRF
    if (this.isDevMode()) {
      return 'dev-token';
    }

    // Check if CSRF token is already available
    const existingToken = getCsrfToken();
    if (existingToken) {
      logger.debug('API Client', 'CSRF token already available');
      return existingToken;
    }

    // Prevent concurrent CSRF fetches (Client-side only)
    if (typeof window !== 'undefined' && this.isFetchingCsrf) {
      logger.debug('API Client', 'CSRF fetch already in progress, waiting...');
      // Bounded wait: at most one TIMEOUT_MS of polling, then proceed with
      // whatever token exists (previously unbounded).
      const maxPolls = Math.ceil(API_CONFIG.TIMEOUT_MS / AUTH_WAIT_POLL_INTERVAL_MS);
      for (let i = 0; i < maxPolls && this.isFetchingCsrf; i++) {
        await new Promise((resolve) => setTimeout(resolve, AUTH_WAIT_POLL_INTERVAL_MS));
      }
      // Null is handled by the caller: the request proceeds without CSRF and
      // the backend rejects with 403 if it matters.
      return getCsrfToken();
    }

    this.isFetchingCsrf = true;

    try {
      logger.info('API Client', 'CSRF token missing, fetching from authenticated endpoint...');

      // ✅ SSR MIGRATION: Use provided headers (containing cookies)
      const headers: Record<string, string> = {
        ...this.config.headers,
        ...requestHeaders,
      };

      logger.debug(
        'API Client',
        'Calling /v1/auth/session to fetch CSRF token (using cookie auth)'
      );

      // Through fetchWithTimeout so a dead backend cannot hang CSRF boot forever.
      const fetchResult = await this.fetchWithTimeout(`${this.config.baseURL}/v1/auth/session`, {
        method: 'GET',
        credentials: this.config.credentials as RequestCredentials,
        headers,
      });
      if (!fetchResult.ok) {
        logger.error('API Client', 'Error fetching CSRF token', { error: fetchResult.error });
        return null;
      }
      const response = fetchResult.value;

      logger.debug('API Client', 'CSRF fetch response', {
        status: response.status,
        ok: response.ok,
        hasCsrfHeader: response.headers.has('X-CSRF-Token'),
        hasSetCookie: response.headers.has('set-cookie'),
      });

      if (!response.ok) {
        logger.error('API Client', 'Failed to fetch CSRF token', {
          status: response.status,
          statusText: response.statusText,
        });
        return null;
      }

      // Extract CSRF token from response header (set by CSRF middleware)
      const csrfTokenFromHeader = response.headers.get('X-CSRF-Token');
      if (csrfTokenFromHeader) {
        setCsrfToken(csrfTokenFromHeader);
        logger.info(
          'API Client',
          'CSRF token fetched from response header and stored successfully'
        );
        return csrfTokenFromHeader;
      }

      // Check Set-Cookie header
      const setCookie = response.headers.get('set-cookie');
      if (setCookie) {
        const csrfTokenName = getCookieName('csrf_token');
        const token = extractSetCookieValue([setCookie], csrfTokenName);
        if (token) {
          setCsrfToken(token);
          logger.info(
            'API Client',
            'CSRF token fetched from Set-Cookie header and stored successfully'
          );
          return token;
        }
      }

      // Try to extract from response body as fallback
      try {
        const text = await response.text();
        const data = JSON.parse(text);
        if (data.csrf_token) {
          setCsrfToken(data.csrf_token);
          logger.info(
            'API Client',
            'CSRF token fetched from response body and stored successfully'
          );
          return data.csrf_token;
        }
      } catch (e) {
        logger.error('API Client', 'Failed to parse CSRF response body', {
          error: e,
        });
      }

      logger.warn('API Client', 'CSRF token not found in response');
      return null;
    } catch (error) {
      logger.error('API Client', 'Error fetching CSRF token', { error });
      return null;
    } finally {
      this.isFetchingCsrf = false;
    }
  }

  /**
   * Refresh access token using refresh cookie
   * Returns a RefreshOutcome: 'refreshed' on success, 'unauthorized' when the
   * refresh token is invalid/revoked, 'conflict' when another request rotated
   * it, or 'failed' on a transient error.
   * ✅ SSR MIGRATION: Simplified - no longer stores tokens in memory
   */
  private async refreshAccessToken(): Promise<RefreshOutcome> {
    // In dev skip-auth mode, no refresh needed
    if (this.isDevMode()) {
      return { status: 'refreshed' };
    }

    // Prevent concurrent refresh calls
    if (this.isRefreshing) {
      logger.warn('API Client', 'Token refresh already in progress, waiting...');
      // Bounded wait: at most one TIMEOUT_MS of polling (previously unbounded).
      const maxPolls = Math.ceil(API_CONFIG.TIMEOUT_MS / AUTH_WAIT_POLL_INTERVAL_MS);
      for (let i = 0; i < maxPolls && this.isRefreshing; i++) {
        await new Promise((resolve) => setTimeout(resolve, AUTH_WAIT_POLL_INTERVAL_MS));
      }
      if (this.isRefreshing) {
        // The in-flight refresh is stuck past its timeout budget. Report a
        // transient failure (callers treat 'failed' as retryable) instead of
        // hanging forever, and never mirror a stale lastRefreshOutcome.
        logger.warn('API Client', 'Timed out waiting for in-flight token refresh');
        return { status: 'failed' };
      }
      // Mirror the real outcome of the refresh that just completed. A blanket
      // 'refreshed' masked concurrent failures: a waiter reported success after
      // a sibling refresh actually failed or was revoked, so the caller retried
      // the original request against a dead session.
      return this.lastRefreshOutcome;
    }

    this.isRefreshing = true;

    try {
      logger.info('API Client', 'Attempting to refresh access token...');

      // Build refresh headers: use SSR-forwarded cookies during server-side rendering,
      // or fall back to credentials: 'include' for client-side (browser sends cookies)
      const isServer = import.meta.env.SSR || typeof window === 'undefined';
      const refreshHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (isServer) {
        // Server-side auth must use ssr-fetch/sessionLoader, which forwards the
        // backend response cookies. This client cannot safely do that.
        logger.error('API Client', 'Blocked client-only refresh during SSR');
        this.lastRefreshOutcome = { status: 'failed' };
        return this.lastRefreshOutcome;
      }

      // Through fetchWithTimeout so a dead backend cannot park the refresh
      // mutex (and every waiter on it) forever.
      const refreshResult = await this.fetchWithTimeout(
        `${this.config.baseURL}${API_ENDPOINTS.AUTH.REFRESH}`,
        {
          method: 'POST',
          credentials: 'include', // the isServer case returned above
          headers: refreshHeaders,
        }
      );
      if (!refreshResult.ok) {
        // Timeout/network are transient by definition: the caller may retry.
        logger.warn('API Client', 'Token refresh failed', {
          error: refreshResult.error,
        });
        this.lastRefreshOutcome = { status: 'failed' };
        return this.lastRefreshOutcome;
      }
      const refreshResponse = refreshResult.value;

      if (!refreshResponse.ok) {
        logger.warn('API Client', 'Token refresh failed', {
          status: refreshResponse.status,
        });
        // 401/403 => refresh token itself is invalid/revoked (the session is
        // gone server-side). Anything else (5xx, 0) is transient -> let the
        // caller retry.
        if (refreshResponse.status === 409) {
          this.lastRefreshOutcome = {
            status: 'conflict',
            retryAfterMs: parseRefreshRetryAfter(refreshResponse.headers.get('Retry-After')),
          };
          return this.lastRefreshOutcome;
        }
        this.lastRefreshOutcome =
          refreshResponse.status === 401 || refreshResponse.status === 403
            ? { status: 'unauthorized' }
            : { status: 'failed' };
        return this.lastRefreshOutcome;
      }

      // Capture a rotated CSRF token from the refresh response so subsequent
      // mutations don't fail the double-submit check. (When the proactive hook
      // routed through apiClient it lost the per-response extraction the old
      // raw fetch did; centralising it here covers both callers.)
      const refreshedCsrf = refreshResponse.headers.get('X-CSRF-Token');
      if (refreshedCsrf) {
        setCsrfToken(refreshedCsrf);
      }

      // ✅ SSR MIGRATION: Backend sets access_token cookie automatically
      // No need to extract from response body or store in memory
      logger.info('API Client', 'Access token refreshed successfully (cookie set by backend)');
      this.lastRefreshOutcome = { status: 'refreshed' };
      return this.lastRefreshOutcome;
    } catch (error) {
      logger.error('API Client', 'Token refresh error', { error });
      this.lastRefreshOutcome = { status: 'failed' };
      return this.lastRefreshOutcome;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Public entry point for proactive token refresh (e.g. useTokenRefresh hook).
   * Routes through refreshAccessToken() so it shares the isRefreshing mutex —
   * this de-dupes against in-flight refreshes triggered by a 401 during normal
   * API calls, preventing two concurrent /v1/auth/refresh requests racing on
   * refresh-token rotation (which would spuriously 401 and log the user out).
   * Returns a RefreshOutcome so the caller can distinguish a real logout
   * ('unauthorized') from a benign rotation race ('conflict') and a transient
   * failure ('failed'). See audit M4.
   */
  async refreshAuth(): Promise<RefreshOutcome> {
    return this.refreshAccessToken();
  }

  /**
   * Test seam: release the refresh mutex. refreshAccessToken() always clears
   * isRefreshing in its finally block, so production code never needs this; it
   * exists so tests can guarantee a clean slate between cases.
   */
  resetRefreshMutex(): void {
    this.isRefreshing = false;
  }

  private async fetchWithTimeout(
    url: string,
    options: RequestOptions
  ): Promise<Result<Response, ApiError>> {
    const timeout = options.timeout || this.config.timeout!;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    // Callers (e.g. debounced validators) may abort mid-flight; compose their
    // signal with the timeout so either source aborts the one real fetch.
    const onCallerAbort = () => controller.abort();
    options.signal?.addEventListener('abort', onCallerAbort);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });
      globalThis.clearTimeout(timeoutId);
      return ok(response);
    } catch (error) {
      globalThis.clearTimeout(timeoutId);

      // Handle abort/timeout
      if (error instanceof Error && error.name === 'AbortError') {
        return err({
          type: 'timeout',
          message: `Request timed out after ${timeout}ms`,
          timeoutMs: timeout,
        });
      }

      // Handle network errors
      return err({
        type: 'network',
        message: error instanceof Error ? error.message : 'Network request failed',
        cause: error,
      });
    } finally {
      options.signal?.removeEventListener('abort', onCallerAbort);
    }
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<Result<T, ApiError>> {
    const { params, retry: retryOption, raw, ...fetchOptions } = options;
    const method = (fetchOptions.method || 'GET').toUpperCase();
    // COR-01: a retry re-sends the request after a timeout or 5xx. For
    // non-idempotent methods the first attempt may already have committed
    // server-side (duplicate community posts/comments/reviews), so automatic
    // retry is restricted to idempotent methods. A caller whose endpoint is
    // safe to replay (e.g. guarded by an idempotency key) can still opt in
    // with an explicit `retry` count.
    const isIdempotentMethod = ['GET', 'HEAD', 'OPTIONS'].includes(method);
    const retry = retryOption ?? (isIdempotentMethod ? API_CONFIG.RETRY_ATTEMPTS : 0);

    // Build URL with query params
    let url = `${this.config.baseURL}${endpoint}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach((item) => searchParams.append(key, String(item)));
          } else {
            searchParams.append(key, String(value));
          }
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
    }

    // Track if we've already tried refreshing for this request
    let hasTriedRefresh = false;
    // Keep the successful/conflict recovery state through the one allowed
    // protected-resource retry. A later 401 cannot safely be reclassified as
    // invalid credentials unless the refresh endpoint itself said so.
    let refreshRecovery: 'refreshed' | 'conflict' | null = null;
    // True when refreshAccessToken returned 'unauthorized': the next parsed
    // error is a definitive revocation, not a data-level 401.
    let refreshDefinitivelyRejected = false;
    // Skip auto-refresh for refresh endpoint itself (avoid infinite loop)
    const isRefreshEndpoint = endpoint === API_ENDPOINTS.AUTH.REFRESH;

    // Merge headers
    const headers: Record<string, string> = {
      ...this.config.headers,
      ...fetchOptions.headers,
    } as Record<string, string>;

    // ✅ SSR Cookie Forwarding: During server-side rendering, automatically
    const isServer = import.meta.env.SSR || typeof window === 'undefined';

    // NOTE: apiClient does NOT forward SSR cookies here. An earlier version tried
    // to read a globalThis.__REQUEST that was never populated (dead code that only
    // emitted warnings). Authenticated SSR data fetches instead go through server
    // fns (lib/server-fns/ssr-fetch.ts) which use TanStack Start's real
    // getRequest(); apiClient is only used for unauthenticated SSR calls (e.g.
    // Telegram auth init) that need no cookie. See audit L1.

    // Let the browser set multipart boundaries for FormData bodies
    if (fetchOptions.body instanceof FormData && headers['Content-Type']) {
      delete headers['Content-Type'];
    } else if (
      fetchOptions.body &&
      typeof fetchOptions.body !== 'string' &&
      !(fetchOptions.body instanceof FormData)
    ) {
      fetchOptions.body = JSON.stringify(fetchOptions.body);
    }

    const isStateChanging = !isIdempotentMethod;
    if (isStateChanging) {
      let csrfToken = headers['X-CSRF-Token'] || getCsrfToken();

      if (!csrfToken) {
        logger.warn(
          'API Client',
          'CSRF token missing for state-changing request, attempting to fetch...',
          { endpoint, method: fetchOptions.method }
        );

        const fetchedToken = await this.ensureCsrfToken(headers);
        if (fetchedToken) {
          csrfToken = fetchedToken;
          logger.info('API Client', 'Successfully fetched CSRF token, retrying request');
        } else {
          logger.error('API Client', 'Failed to fetch CSRF token from backend', {
            endpoint,
            method: fetchOptions.method,
          });
        }
      }

      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
        logger.debug('API Client', 'Including CSRF token in request', {
          endpoint,
          method: fetchOptions.method,
          tokenLength: csrfToken.length,
        });
      } else {
        // CSRF token still missing - log but continue
        // The request will likely fail with 403, but we've tried our best
        logger.error(
          'API Client',
          'CSRF token unavailable for state-changing request - proceeding without it',
          {
            endpoint,
            method: fetchOptions.method,
            hint: "Ensure you've logged in and the page has loaded completely. Try refreshing the page.",
          }
        );
        // Continue without token - backend will reject with 403 if needed
      }
    }

    let lastError: ApiError | null = null;

    // Debug logging for authentication
    logger.info('API Client', 'Request attempt', {
      url,
      method: fetchOptions.method,
      headers: {
        cookie: headers['cookie'] ? '[REDACTED]' : 'none',
        'X-CSRF-Token': headers['X-CSRF-Token'] ? '[REDACTED]' : 'none',
        'Content-Type': headers['Content-Type'],
      },
      isRefreshEndpoint,
      hasTriedRefresh,
    });

    // Retry logic with exponential backoff
    for (let attempt = 0; attempt <= retry; attempt++) {
      if (attempt === 0) {
        logger.debug('API Client', 'Sending request', {
          url,
          method: fetchOptions.method,
          headerNames: Object.keys(headers),
          hasBody: !!fetchOptions.body,
        });
      }

      const fetchResult = await this.fetchWithTimeout(url, {
        ...fetchOptions,
        headers,
        // ✅ SSR FIX: Only use credentials option for client-side requests
        // During SSR, we manually forward cookies via Cookie header
        credentials: isServer ? undefined : (fetchOptions.credentials ?? this.config.credentials),
      });

      // If fetch itself failed, handle it
      if (!fetchResult.ok) {
        lastError = fetchResult.error;

        // A caller-aborted request (e.g. a superseded debounce check) is not a
        // failure to retry: the caller no longer wants the response, so return
        // immediately instead of sleeping through the backoff ladder.
        if (fetchOptions.signal?.aborted) {
          return fetchResult;
        }

        // Don't retry on client errors (4xx) or last attempt
        if (
          fetchResult.error.type === 'unauthorized' ||
          fetchResult.error.type === 'forbidden' ||
          fetchResult.error.type === 'not_found' ||
          fetchResult.error.type === 'validation' ||
          attempt === retry
        ) {
          logger.apiError(endpoint, fetchResult.error, { attempt, url });
          return fetchResult;
        }

        // Exponential backoff for retryable errors
        const delay = Math.min(
          API_CONFIG.RETRY_BASE_DELAY_MS * Math.pow(2, attempt),
          API_CONFIG.RETRY_MAX_DELAY_MS
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      const response = fetchResult.value;

      // Handle HTTP errors
      if (!response.ok) {
        // ✅ ENHANCEMENT: Auto-refresh token on 401 Unauthorized
        if (
          response.status === HTTP_STATUS.UNAUTHORIZED &&
          !isRefreshEndpoint &&
          !hasTriedRefresh &&
          !fetchOptions.skipAuthRefresh
        ) {
          logger.info('API Client', '401 Unauthorized, attempting token refresh...');
          hasTriedRefresh = true;

          const refreshOutcome = await this.refreshAccessToken();

          if (refreshOutcome.status === 'refreshed' || refreshOutcome.status === 'conflict') {
            refreshRecovery = refreshOutcome.status;
            if (refreshOutcome.status === 'conflict') {
              // The refresh winner may be another tab. Do not clear the local
              // session or issue another refresh: retry this original request
              // once after its Set-Cookie response has reached the cookie jar.
              logger.info('API Client', 'Refresh rotation conflict; retrying original request');
              await waitForRefreshConflict(refreshOutcome.retryAfterMs);
            }
            // ✅ SSR MIGRATION: No need to update headers manually
            // New access token cookie is already set by refresh endpoint
            logger.info('API Client', 'Retrying request with refreshed token (from cookie)');
            // Retry without consuming a retry slot. `continue` still runs the
            // for-loop increment, so reset attempt to -1 so it becomes 0 again.
            // (Previously a bare `continue` burned a slot and, with retry=0, the
            // refreshed request was never sent. hasTriedRefresh guards against
            // infinite refresh loops.) See audit L2.
            attempt = -1;
            continue;
          }

          if (refreshOutcome.status === 'failed') {
            logger.warn('API Client', 'Token refresh failed transiently');
            return err({
              type: 'server',
              status: 503,
              code: 'AUTH_REFRESH_FAILED',
              message: 'Session refresh temporarily unavailable',
            });
          }
          // Only a definitive 401/403 from the refresh endpoint reaches the
          // normal protected-resource 401 parser below. Flag it so the parser
          // can mark the error as a definitive revocation, not a data 401 (S1).
          refreshDefinitivelyRejected = true;
        }

        if (
          response.status === HTTP_STATUS.UNAUTHORIZED &&
          hasTriedRefresh &&
          refreshRecovery !== null
        ) {
          if (refreshRecovery === 'conflict') {
            return err({
              type: 'conflict',
              status: 409,
              message: 'Session refresh conflict is still resolving',
              resource: 'auth_session',
            });
          }

          return err({
            type: 'server',
            status: 503,
            code: 'AUTH_SESSION_RETRY_FAILED',
            message: 'Session recovery retry was not accepted',
          });
        }

        // Clone response for error parsing (response can only be read once)
        const responseClone = response.clone();
        const apiError = await fromHttpResponse(responseClone);
        lastError = apiError;

        if (refreshDefinitivelyRejected) {
          // Swap in the canonical message: hooks re-wrap errors as
          // new Error(error.message), so the message is what survives to
          // QueryCache.onError (see SESSION_REVOKED_ERROR_MESSAGE).
          apiError.message = SESSION_REVOKED_ERROR_MESSAGE;
        }

        // Don't retry on client errors (4xx) or last attempt
        if (
          response.status >= HTTP_STATUS.BAD_REQUEST &&
          response.status < HTTP_STATUS.INTERNAL_SERVER_ERROR
        ) {
          logger.apiError(endpoint, apiError, { status: response.status, url });
          return err(apiError);
        }

        if (attempt === retry) {
          logger.apiError(endpoint, apiError, {
            attempt,
            status: response.status,
            url,
          });
          return err(apiError);
        }

        // Exponential backoff
        const delay = Math.min(
          API_CONFIG.RETRY_BASE_DELAY_MS * Math.pow(2, attempt),
          API_CONFIG.RETRY_MAX_DELAY_MS
        );
        await new Promise((resolve) => setTimeout(resolve, delay));
        continue;
      }

      // Handle successful response
      try {
        // ✅ CSRF Token Rotation: Extract and store CSRF token from response header
        // Backend sends CSRF token in X-CSRF-Token header (cookie is HttpOnly, so we can't read it)
        const newCsrfToken = response.headers.get('X-CSRF-Token');
        if (newCsrfToken) {
          const oldToken = getCsrfToken();
          setCsrfToken(newCsrfToken);
          logger.debug('API Client', 'Updated CSRF token from response header', {
            endpoint,
            tokenLength: newCsrfToken.length,
            tokenChanged: oldToken !== newCsrfToken,
          });
        }

        // Check if response has content
        const contentLength = response.headers.get('content-length');
        const hasContent = contentLength !== '0' && contentLength !== null;

        // Handle empty responses (204 No Content, etc.)
        if (!hasContent && response.status === 204) {
          return ok({} as T);
        }

        // Check content type before parsing
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          // Try to read text to see if there's any content
          const text = await response.text();
          if (!text || text.trim() === '') {
            return ok({} as T);
          }
          // A proxy/WAF (or misconfigured route) can answer 2xx with an HTML
          // error page. Previously this was dropped and treated as an empty
          // success, which made auth calls like /v1/auth/me look "ok but empty"
          // and silently log the user out. Surface it as a parse error instead.
          // The catch below logs it via logger.apiError and returns err().
          throw new Error(
            `Expected JSON response but received ${contentType || 'no Content-Type'} ` +
              `(${text.length} bytes). Body starts with: ${text.slice(0, 200)}`
          );
        }

        // Parse JSON response - read as text first to handle empty responses
        const text = await response.text();
        if (!text || text.trim() === '') {
          // Empty JSON response
          return ok({} as T);
        }

        const data = JSON.parse(text) as T;

        // ✅ Also check response body for CSRF token (auth endpoints return it in body)
        if (typeof data === 'object' && data !== null && 'csrf_token' in data) {
          const csrfTokenFromBody = (data as { csrf_token?: string }).csrf_token;
          if (csrfTokenFromBody) {
            setCsrfToken(csrfTokenFromBody);
            logger.debug('API Client', 'Stored CSRF token from response body', {
              endpoint,
            });
          }
        }

        // ✅ Extract data field from APIResponse wrapper (backend standard format)
        // Backend returns one envelope: { data: ..., requestId: ... }
        // We want to return just the data field for type safety and cleaner API
        // Only extract if it has ALL 4 APIResponse fields to avoid false positives.
        // `raw` callers (e.g. cursor pagination) opt out to keep envelope siblings
        // like `pagination`, which `WriteSuccessWithPagination` puts beside `data`.
        let responseData = data;
        if (
          !raw &&
          typeof data === 'object' &&
          data !== null &&
          'data' in data &&
          'requestId' in data &&
          typeof (data as { data: unknown }).data === 'object'
        ) {
          responseData = (data as { data: T }).data as T;
          logger.debug('API Client', 'Extracted data field from APIResponse wrapper', {
            endpoint,
            hasData: !!responseData,
          });
        }

        return ok(responseData);
      } catch (error) {
        const parseError: ApiError = {
          type: 'parse',
          message: error instanceof Error ? error.message : 'Failed to parse response JSON',
          cause: error,
        };
        logger.apiError(endpoint, parseError, { url });
        return err(parseError);
      }
    }

    // Should never reach here, but TypeScript needs this
    return err(
      lastError || {
        type: 'network',
        message: 'Request failed after all retry attempts',
      }
    );
  }

  /**
   * Perform GET request
   */
  async get<T>(endpoint: string, options?: RequestOptions): Promise<Result<T, ApiError>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * GET returning the full parsed envelope (no `data` extraction) — for callers
   * that need envelope siblings such as `pagination`. See `RequestOptions.raw`.
   */
  async getRaw<T>(endpoint: string, options?: RequestOptions): Promise<Result<T, ApiError>> {
    return this.request<T>(endpoint, { ...options, method: 'GET', raw: true });
  }

  /**
   * Perform POST request
   */
  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<Result<T, ApiError>> {
    if (data instanceof FormData) {
      return this.request<T>(endpoint, {
        ...options,
        method: 'POST',
        body: data,
      });
    }

    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data as BodyInit,
    });
  }

  /**
   * Perform PUT request
   */
  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<Result<T, ApiError>> {
    if (data instanceof FormData) {
      return this.request<T>(endpoint, {
        ...options,
        method: 'PUT',
        body: data,
      });
    }

    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data as BodyInit,
    });
  }

  /**
   * Perform PATCH request
   */
  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<Result<T, ApiError>> {
    if (data instanceof FormData) {
      return this.request<T>(endpoint, {
        ...options,
        method: 'PATCH',
        body: data,
      });
    }

    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data as BodyInit,
    });
  }

  /**
   * Perform DELETE request
   */
  async delete<T>(endpoint: string, options?: RequestOptions): Promise<Result<T, ApiError>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }
}

// Create a function to get the API client instance with current environment
function createApiClient() {
  return new ApiClient({
    baseURL: API_CONFIG.BASE_URL,
    credentials: API_CONFIG.CREDENTIALS,
  });
}

// Export singleton instance created with current environment
export const apiClient = createApiClient();
